import React, { useEffect, useState } from 'react';

import { Mail, Send } from 'lucide-react';
import { useMutation, useQuery, queryCache } from 'react-query';
import { DotBricksLoading, ListPagination, Toast } from 'src/common/components';
import { useDebouncedValue } from 'src/common/hooks';

import { inviteFromWaitlist, fetchWaitlist } from '../api';
import { Paginated, WaitlistEntry } from '../types';
import { formatDateTime, waitlistStatusBadge } from '../utils';

import { CopyableCode } from './CopyableCode';

const STATUS_FILTERS: Array<{ key: string; label: string }> = [
  { key: '', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'invited', label: 'Invited' },
  { key: 'converted', label: 'Converted' },
];

const WaitlistRow: React.FC<{ entry: WaitlistEntry }> = ({ entry }) => {
  const [invite, { isLoading: inviting }] = useMutation(() => inviteFromWaitlist(entry.id, {}), {
    onSuccess: (res) => {
      const { invite_code: code, email_sent: emailSent } = res.data;
      // A missing code is a server-side problem, not something to paper over
      // with an empty string — the operator needs to know the invite is not
      // usable yet.
      if (!code) {
        Toast.error(`Invited ${entry.email}, but no code came back — check cosmos.`);
      } else {
        Toast.success(
          emailSent
            ? `Invited ${entry.email} with code ${code.code}.`
            : `${entry.email} was already invited — their code is ${code.code}.`
        );
      }
      queryCache.invalidateQueries('waitlist');
    },
    onError: () => {
      Toast.error('Could not invite this person.');
    },
  });

  const badge = waitlistStatusBadge(entry.status);

  return (
    <tr className="hover:bg-gray-50 border-t border-gray-200">
      <td className="px-4 py-2 text-sm text-gray-900">{entry.email}</td>
      <td className="px-4 py-2 text-sm text-gray-600">{entry.phone || '—'}</td>
      <td className="px-4 py-2 text-sm text-gray-600">{entry.city || '—'}</td>
      <td className="px-4 py-2 text-sm text-gray-500">{formatDateTime(entry.created_at)}</td>
      <td className="px-4 py-2 text-sm">
        <span
          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium ${badge.classes}`}
        >
          {entry.status !== 'pending' && <Mail size={12} />}
          {badge.label}
        </span>
      </td>
      <td className="px-4 py-2 text-sm">
        {entry.invite_code ? <CopyableCode code={entry.invite_code} /> : <span>—</span>}
      </td>
      <td className="px-4 py-2 text-sm text-right">
        {entry.status === 'pending' && (
          <button
            type="button"
            onClick={() => invite()}
            disabled={inviting}
            className="bg-primary-2.0 hover:bg-primary-3.0 disabled:opacity-50 inline-flex items-center gap-1 px-2.5 py-1 text-xs text-white rounded"
            aria-label="Invite"
          >
            <Send size={12} />
            {inviting ? 'Inviting…' : 'Invite'}
          </button>
        )}
      </td>
    </tr>
  );
};

export const Waitlist: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState('');
  const search = useDebouncedValue(searchInput);

  useEffect(() => setPage(1), [search]);

  const { data, isLoading, isError } = useQuery<{ data: Paginated<WaitlistEntry> }>(
    ['waitlist', page, search, status],
    () => fetchWaitlist({ page, search, status }),
    { keepPreviousData: true }
  );

  const entries = data?.data?.results ?? [];
  const totalCount = data?.data?.count ?? 0;

  return (
    <div className="sm:px-6 lg:px-8 flex flex-col h-full pt-8 pb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Waitlist</h2>
        <input
          type="search"
          placeholder="Search email, phone, city…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-2.0 w-64"
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key || 'all'}
            type="button"
            onClick={() => {
              setStatus(f.key);
              setPage(1);
            }}
            className={`px-3 py-1 rounded text-sm border transition-colors ${
              status === f.key
                ? 'bg-primary-2.0 text-white border-primary-2.0'
                : 'bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <DotBricksLoading />
      ) : isError ? (
        <p className="text-red-600">Failed to load the waitlist. Please try again.</p>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <p className="text-base font-medium">Waitlist is empty</p>
          <p className="mt-1 text-sm">People who request access will show up here.</p>
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-auto border border-gray-200 rounded shadow-sm">
            <table className="min-w-full text-left">
              <thead className="bg-gray-50 sticky top-0 text-xs tracking-wider text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Phone</th>
                  <th className="px-4 py-2">City</th>
                  <th className="px-4 py-2">Requested</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {entries.map((entry) => (
                  <WaitlistRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
          <ListPagination totalCount={totalCount} currentPage={page} setCurrentPage={setPage} />
        </div>
      )}
    </div>
  );
};
